import FormModal from '@/components/common/Modal/FormModal';
import { useErrorContext } from '@/context/ErrorContext';
import { AlbumState } from '@/data/models/albumState';
import useReplicateProductMutation, {
  CmsProductType,
} from '@/hooks/useReplicateProductMutation';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductType, ProductTypeSelector } from './ProductTypeSelector';
import styles from './DuplicateForDigitalModal.module.scss';
import { getCmsProductUrl } from '@/helpers/productUrls';

type DuplicateToDigitalModalProps = {
  show: boolean;
  onClose: () => void;
  albumId: string;
  albumState: AlbumState;
};

export default function DuplicateToDigitalModal({
  show,
  onClose,
  albumId,
  albumState,
}: DuplicateToDigitalModalProps) {
  const { t } = useTranslation();
  const [productType, setProductType] = useState<ProductType>('Audio');
  const replicateProduct = useReplicateProductMutation();
  const { setError } = useErrorContext();

  const submit = () => {
    if (!productType) return;
    const cmsProductType = getCmsProductType(productType);

    replicateProduct.mutate(
      {
        albumId,
        productType: cmsProductType,
        state: albumState,
      },
      {
        onSuccess: (cmsProduct) => {
          if (cmsProduct?.data?.id) {
            window.open(
              getCmsProductUrl(cmsProduct.data.id, cmsProductType),
              '_blank'
            );
            onClose();
          }
        },
        onError: () => {
          setError(t('common.error.defaultError'));
          onClose();
        },
      }
    );
  };

  return (
    <FormModal
      show={show}
      onClose={onClose}
      onSubmit={submit}
      titleText={t('productActions.dropdown.menu.duplicateForDigital')}
      submitButtonText={t('product.duplicateForDigitalModal.button')}
      submitButtonDisabled={replicateProduct.isPending}
      dialogClassName={styles.modal}
    >
      <label className="modal-field-label upper-case">
        {t('product.duplicateForDigitalModal.body')}
      </label>
      <div>
        <ProductTypeSelector onChange={setProductType} />
      </div>
    </FormModal>
  );
}

function getCmsProductType(productType: ProductType): CmsProductType {
  switch (productType) {
    case 'Music Video':
      return 'MusicVideo';
    case 'A/V Bundle':
      return 'AudioVideoAlbum';
    default:
      return 'AudioAlbum';
  }
}
